<script setup>
import { computed, ref, watch } from 'vue';
import { isSendableTemplate } from '@chatwoot/utils';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildContractTemplateParams,
  carriesLink,
  dynamicUrlButton,
  firstName,
  renderTemplateText,
  templateFields,
} from './contractLinkTemplate';

// Escolha do modelo aprovado para enviar o link do contrato pelo WhatsApp. Fora da janela de
// 24 h (`required`) a mensagem simples some: a Meta só aceita modelo aprovado.
const props = defineProps({
  inboxId: { type: [Number, String], default: '' },
  recipientName: { type: String, default: '' },
  publicUrl: { type: String, default: '' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // Rótulo da opção sem template (ex.: a mensagem configurada no modelo do contrato).
  plainLabel: { type: String, default: '' },
});

const emit = defineEmits(['change']);

const copy = {
  label: 'Modelo de mensagem',
  plain: 'Enviar mensagem simples',
  placeholder: 'Buscar modelo aprovado',
  fetch: 'Buscar modelos na Meta',
  fetching: 'Buscando modelos…',
  loading: 'Carregando modelos…',
  loadError: 'Não foi possível carregar os modelos desta caixa.',
  noApproved: 'Nenhum modelo aprovado nesta caixa.',
  approvedCount: count =>
    count === 1 ? '1 modelo aprovado' : `${count} modelos aprovados`,
  pending: names =>
    `Aguardando aprovação da Meta: ${names}. Quando aprovado, use "Buscar modelos na Meta".`,
  fetched: 'Modelos atualizados.',
  fetchStarted:
    'A Meta está devolvendo os modelos. Se o seu não aparecer, tente de novo em instantes.',
  variable: key => `Variável {{${key}}}`,
  header: 'cabeçalho',
  useName: 'Usar nome',
  useLink: 'Usar link',
  preview: 'Prévia',
  buttonLink: 'O botão do modelo abre o link deste contrato.',
  noLinkStatic:
    'Este modelo não leva o link do contrato: o botão aponta para um endereço fixo e nenhuma variável recebe o link. Use "Usar link" numa variável ou ajuste o botão em Templates da Meta para URL dinâmica (https://link.talkvia.com.br/{{1}}).',
  noLinkBase: base =>
    `O botão de URL dinâmica começa com ${base}, que não é o endereço deste contrato. Use "Usar link" numa variável ou ajuste o botão para https://link.talkvia.com.br/{{1}}.`,
  missingValues: 'Preencha todas as variáveis do modelo.',
};

const templates = ref([]);
const isLoading = ref(false);
const isFetching = ref(false);
const loadFailed = ref(false);
const selectedKey = ref('');
const fields = ref([]);
let requestId = 0;

const templateKey = template =>
  `${template.name || template.friendly_name}::${template.language || ''}`;

const sendableTemplates = computed(() =>
  templates.value.filter(isSendableTemplate)
);
const pendingNames = computed(() =>
  templates.value
    .filter(template =>
      ['PENDING', 'IN_APPEAL'].includes(String(template.status).toUpperCase())
    )
    .map(template => template.name)
    .join(', ')
);

const options = computed(() => [
  ...(props.required
    ? []
    : [{ value: '', label: props.plainLabel || copy.plain }]),
  ...sendableTemplates.value.map(template => ({
    value: templateKey(template),
    label: `${template.name || template.friendly_name} · ${template.language || 'pt_BR'}`,
  })),
]);

const selectedTemplate = computed(
  () =>
    sendableTemplates.value.find(
      template => templateKey(template) === selectedKey.value
    ) || null
);

const linkButton = computed(() =>
  selectedTemplate.value
    ? dynamicUrlButton(selectedTemplate.value, props.publicUrl)
    : null
);
const hasLink = computed(
  () =>
    !selectedTemplate.value ||
    carriesLink(selectedTemplate.value, fields.value, props.publicUrl)
);
const missingValues = computed(() =>
  fields.value.some(field => !String(field.value || '').trim())
);
const noLinkMessage = computed(() => {
  if (hasLink.value) return '';
  return linkButton.value && !linkButton.value.matches
    ? copy.noLinkBase(linkButton.value.base)
    : copy.noLinkStatic;
});
const preview = computed(() =>
  selectedTemplate.value
    ? renderTemplateText(selectedTemplate.value, fields.value)
    : ''
);

const emitChange = () => {
  const template = selectedTemplate.value;
  emit('change', {
    templateParams: template
      ? buildContractTemplateParams(template, fields.value, props.publicUrl)
      : null,
    content: template ? preview.value : '',
    ready: template
      ? hasLink.value && !missingValues.value
      : !props.required && !isLoading.value,
  });
};

const loadTemplates = async () => {
  requestId += 1;
  const currentRequest = requestId;
  if (!props.inboxId) {
    templates.value = [];
    return null;
  }
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const { data } = await InboxesAPI.getMessageTemplates(props.inboxId);
    if (currentRequest !== requestId) return null;
    templates.value = Array.isArray(data?.payload) ? data.payload : [];
    return data?.meta?.last_sync_attempt_at || null;
  } catch {
    if (currentRequest !== requestId) return null;
    templates.value = [];
    loadFailed.value = true;
    return null;
  } finally {
    if (currentRequest === requestId) {
      isLoading.value = false;
      emitChange();
    }
  }
};

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Pede à Meta a lista atual e acompanha até a sincronização terminar (no máximo ~12 s).
const fetchFromMeta = async () => {
  if (!props.inboxId || isFetching.value) return;
  isFetching.value = true;
  const previousSync = await loadTemplates();
  try {
    await InboxesAPI.syncTemplates(props.inboxId);
  } catch {
    // Sem permissão para sincronizar, a lista já sincronizada continua valendo.
  }
  let updated = false;
  for (let attempt = 0; attempt < 6 && !updated; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(2000);
    // eslint-disable-next-line no-await-in-loop
    const lastSync = await loadTemplates();
    updated = Boolean(lastSync) && lastSync !== previousSync;
  }
  isFetching.value = false;
  useAlert(updated ? copy.fetched : copy.fetchStarted);
};

const setFieldValue = (field, value) => {
  field.value = value;
  emitChange();
};

watch(
  () => props.inboxId,
  () => {
    selectedKey.value = '';
    fields.value = [];
    loadTemplates();
  },
  { immediate: true }
);

watch(selectedKey, () => {
  fields.value = selectedTemplate.value
    ? templateFields(selectedTemplate.value, {
        recipientName: props.recipientName,
        publicUrl: props.publicUrl,
      })
    : [];
  emitChange();
});

watch(() => props.required, emitChange);
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between gap-2">
      <label class="text-xs font-medium text-n-slate-11">{{
        copy.label
      }}</label>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        color="blue"
        icon="i-hugeicons-refresh"
        :label="isFetching ? copy.fetching : copy.fetch"
        :is-loading="isFetching"
        :disabled="disabled || isFetching || !inboxId"
        data-test="fetch-meta-templates"
        @click="fetchFromMeta"
      />
    </div>
    <p
      v-if="isLoading && !templates.length"
      class="mb-0 text-xs text-n-slate-11"
      role="status"
    >
      {{ copy.loading }}
    </p>
    <template v-else>
      <ComboBox
        v-model="selectedKey"
        :options="options"
        :placeholder="copy.placeholder"
        :disabled="disabled || !options.length"
        teleport-to-body
      />
      <p class="mb-0 text-xs text-n-slate-11">
        {{
          loadFailed
            ? copy.loadError
            : sendableTemplates.length
              ? copy.approvedCount(sendableTemplates.length)
              : copy.noApproved
        }}
      </p>
      <p v-if="pendingNames" class="mb-0 text-xs text-n-amber-11">
        {{ copy.pending(pendingNames) }}
      </p>
    </template>

    <div v-if="selectedTemplate" class="space-y-3">
      <div
        v-for="field in fields"
        :key="`${field.component}-${field.key}`"
        class="space-y-1"
      >
        <Input
          :model-value="field.value"
          :label="
            field.component === 'header'
              ? `${copy.variable(field.key)} · ${copy.header}`
              : copy.variable(field.key)
          "
          :disabled="disabled"
          @update:model-value="value => setFieldValue(field, value)"
        />
        <div class="flex gap-2">
          <Button
            type="button"
            size="xs"
            variant="faded"
            color="slate"
            :label="copy.useName"
            :disabled="disabled"
            @click="setFieldValue(field, firstName(recipientName))"
          />
          <Button
            type="button"
            size="xs"
            variant="faded"
            color="slate"
            :label="copy.useLink"
            :disabled="disabled"
            @click="setFieldValue(field, publicUrl)"
          />
        </div>
      </div>
      <p v-if="linkButton?.matches" class="mb-0 text-xs text-n-teal-11">
        {{ copy.buttonLink }}
      </p>
      <p
        v-if="noLinkMessage"
        class="mb-0 rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-3 text-xs leading-relaxed text-n-ruby-11"
        data-test="template-without-link"
      >
        {{ noLinkMessage }}
      </p>
      <p v-else-if="missingValues" class="mb-0 text-xs text-n-ruby-11">
        {{ copy.missingValues }}
      </p>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3">
        <span class="mb-1 block text-xs font-semibold text-n-slate-11">{{
          copy.preview
        }}</span>
        <p class="mb-0 whitespace-pre-line break-words text-sm text-n-slate-12">
          {{ preview }}
        </p>
      </div>
    </div>
  </div>
</template>
